/* eslint-disable no-restricted-globals */
// NOSONAR S7764 — globalThis is not available in this ESLint environment; window is correct here
import PropTypes from "prop-types";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "./CompanyProfile.css";
import { resolveLogoSrc } from "../../utils/logoUtils";

/* ─────────────────────────────────────────
   SHARED CONFIG (mirrors JobsPage exactly)
───────────────────────────────────────── */
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:3000")
  .replace(/\/$/, "")
  .replace(/\/api$/, "");

const LOGO_COLORS = ["#263012","#263012","#111111","#1d240e","#4a5e20","#263012","#2a2a2a","#5c774a","#263012","#6b7280"];

/* ─────────────────────────────────────────
   AUTH / STORAGE  (mirrors JobsPage)
───────────────────────────────────────── */
function readStoredToken() {
  return (
    localStorage.getItem("th_employer_token") ||
    localStorage.getItem("th_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") || ""
  );
}
function readStoredUser() {
  try {
    const r =
      localStorage.getItem("th_employer") ||
      localStorage.getItem("th_user") ||
      localStorage.getItem("user") ||
      localStorage.getItem("authUser");
    return r ? JSON.parse(r) : null;
  } catch { return null; }
}
function saveUser(user, token) {
  try {
    const u = JSON.stringify(user);
    localStorage.setItem("th_user", u);
    localStorage.setItem("user", u);
    localStorage.setItem("th_token", token);
    localStorage.setItem("token", token);
  } catch { /* noop */ }
}
function loadPersistedIds(key) {
  try { const r = localStorage.getItem(key); return r ? new Set(JSON.parse(r)) : new Set(); }
  catch { return new Set(); }
}
function persistIds(key, set) {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch { /* noop */ }
}
function isAdmin() { const u = readStoredUser(); return u?.role === "admin" || u?.isAdmin; }

/* ─────────────────────────────────────────
   API HELPERS
───────────────────────────────────────── */
async function apiFetch(path, options = {}) {
  const token = readStoredToken();
  const url = path.startsWith("http")
    ? path
    : `${API_BASE}${path.startsWith("/api") ? path : `/api${path.startsWith("/") ? path : `/${path}`}`}`;
  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });
  if (!res.ok) {
    let d = ""; try { d = await res.text(); } catch { /* noop */ }
    throw new Error(d ? `API ${res.status}: ${d}` : `API ${res.status}: ${res.statusText}`);
  }
  return res.json();
}

function authFetch(path, opts = {}) {
  const token = readStoredToken();
  return fetch(`${API_BASE}/api${path}`, {
    credentials: "include", ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

async function fetchFullProfile() {
  const token = readStoredToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/api/candidate/profile`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    ["skills","locations","education","courses"].forEach(k => {
      if (typeof data[k] === "string") { try { data[k] = JSON.parse(data[k]); } catch { data[k] = []; } }
    });
    if (data.first_name && !data.firstName)   data.firstName  = data.first_name;
    if (data.last_name  && !data.lastName)    data.lastName   = data.last_name;
    if (data.headline   && !data.bio)         data.bio        = data.headline;
    if (data.mobile     && !data.phone)       data.phone      = data.mobile;
    if (data.work_status && !data.experience) data.experience = data.work_status;
    if (data.country_iso && !data.country)    data.country    = data.country_iso;
    return data;
  } catch { return null; }
}

/* ─────────────────────────────────────────
   PROFILE HELPERS  (mirrors JobsPage)
───────────────────────────────────────── */
function getMissingFields(user) {
  if (!user) return [];
  const m = [];
  if (!(user.name || user.firstName || user.first_name)) m.push({ key:"name",      label:"Full Name",           icon:"👤" });
  if (!user.email)                                        m.push({ key:"email",     label:"Email Address",       icon:"✉️" });
  if (!(user.phone || user.mobile))                       m.push({ key:"phone",     label:"Phone Number",        icon:"📱" });
  const s = user.skills;
  const hasSkills = Array.isArray(s) ? s.length > 0 : (typeof s==="string" ? s.trim()!==""&&s!=="[]" : !!s);
  if (!hasSkills)                                         m.push({ key:"skills",    label:"Skills",              icon:"🛠️" });
  if (!(user.resume_url || user.resumeUrl))               m.push({ key:"resume",    label:"Resume / CV",         icon:"📄" });
  if (!(user.bio||user.headline||user.description||user.about)) m.push({ key:"bio", label:"Short Description",  icon:"📝" });
  if (!(user.aadhar||user.aadhaar||user.aadhar_number))   m.push({ key:"aadhar",   label:"Aadhaar Card Number", icon:"🪪" });
  if (!(user.pan||user.pan_card||user.pan_number))         m.push({ key:"pan",      label:"PAN Card Number",     icon:"💳" });
  const edu = user.education || user.educations || [];
  const hasEdu = Array.isArray(edu) ? edu.length>0&&edu.some(e=>e?.degree||e?.course) : !!user.degree;
  if (!hasEdu)                                            m.push({ key:"education", label:"Education Details",   icon:"🎓" });
  if (!(user.country||user.country_iso||user.countryIso)) m.push({ key:"country",  label:"Country",             icon:"🌍" });
  if (!user.gender)                                       m.push({ key:"gender",    label:"Gender",              icon:"⚧️" });
  return m;
}

function profilePercent(user) {
  if (!user) return 0;
  const sv = (() => {
    const s = user.skills;
    if (Array.isArray(s)) return s.length > 0 ? "y" : null;
    if (typeof s === "string") return s.trim() && s !== "[]" ? "y" : null;
    return s || null;
  })();
  const ev = (() => {
    const e = user.education || user.educations || [];
    if (Array.isArray(e)) return e.length > 0 && e.some(x => x?.degree || x?.course) ? "y" : null;
    return user.degree || null;
  })();
  const fields = [
    user.name || user.firstName || user.first_name,
    user.email,
    user.phone || user.mobile,
    sv,
    user.resume_url || user.resumeUrl,
    user.bio || user.headline || user.description || user.about,
    user.aadhar || user.aadhaar || user.aadhar_number,
    user.pan || user.pan_card || user.pan_number,
    ev,
    user.country || user.country_iso || user.countryIso,
    user.gender,
    user.location || user.city,
    user.experience || user.work_status,
  ];
  return Math.round(fields.filter(v => v && String(v).trim() !== "").length / fields.length * 100);
}

function displayName(user) {
  if (!user) return "";
  return user.name || `${user.firstName || user.first_name || ""} ${user.lastName || user.last_name || ""}`.trim() || user.username || user.email || "";
}

/* ─────────────────────────────────────────
   JOB NORMALISER  (mirrors JobsPage)
───────────────────────────────────────── */
function formatPostedDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const diff = Math.floor((Date.now() - dt) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff <= 6)  return `${diff} days ago`;
  if (diff <= 13) return "1 week ago";
  if (diff <= 20) return "2 weeks ago";
  if (diff <= 27) return "3 weeks ago";
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function isNew(d) { return !!d && Math.floor((Date.now() - new Date(d)) / 86400000) <= 3; }
function companyColor(n) { return LOGO_COLORS[(n || "?").codePointAt(0) % LOGO_COLORS.length]; }

function normaliseJob(raw) {
  let tags = [];
  try {
    const rt = raw.skills || raw.tags || "";
    tags = typeof rt === "string"
      ? rt.startsWith("[") ? JSON.parse(rt) : rt.split(",").map(t => t.trim()).filter(Boolean)
      : Array.isArray(rt) ? rt : [];
  } catch { tags = []; }
  const locLower = (raw.location || "").toLowerCase();
  let locationType = "On-site";
  if (locLower.includes("hybrid")) locationType = "Hybrid";
  else if (locLower.includes("remote")) locationType = "Remote";
  const salaryMin = raw.min_salary || 0;
  const salaryMax = raw.max_salary || 0;
  const sMin = Math.round(salaryMin / 100000);
  const sMax = Math.round(salaryMax / 100000);
  const salaryAvg = sMin && sMax ? Math.round((sMin + sMax) / 2) : sMin || sMax;
  const salaryDisplay = (() => {
    const r = (raw.salary || "").replace(/^[$?₹\u20B9\s]+/, "").trim();
    if (r) return `₹${r}`;
    if (sMin && sMax) return `₹${sMin}–${sMax} LPA`;
    if (salaryAvg) return `₹${salaryAvg} LPA`;
    return "Not disclosed";
  })();
  const postedAt = raw.posted_date || raw.created_at || raw.posted_at || null;
  return {
    id: raw.id || raw._id,
    title: raw.title || "Untitled",
    company: raw.company_name || raw.company || "—",
    dept: raw.category || raw.department || "General",
    type: raw.type || raw.job_type || "Full-time",
    location: raw.location || "",
    locationType,
    salaryMin, salaryMax, salaryAvg, salary: salaryDisplay, tags,
    status: raw.status || "active",
    postedAt,
    postedLabel: formatPostedDate(postedAt),
    isNew: isNew(postedAt),
    description: raw.description || "",
    experience: raw.experience || "",
    openings: Number(raw.openings || raw.vacancies || 1),
    applicantCount: Number(raw.applicant_count || 0),
    recruiter_id: raw.recruiter_id || raw.employer_id || null,
    recruiter_email: raw.recruiter_email || raw.employer_email || null,
    logo: resolveLogoSrc(raw.company_logo || raw.ep_logo || raw.logo || ""),
  };
}

/* ─────────────────────────────────────────
   APPLY API  (mirrors JobsPage exactly)
───────────────────────────────────────── */
async function submitApplicationAPI({ job }) {
  const res = await authFetch("/applications", {
    method: "POST",
    body: JSON.stringify({ job_id: job.id, recruiter_id: job.recruiter_id, applied_at: new Date().toISOString() }),
  });
  let data = {};
  try { data = await res.json(); } catch { /* noop */ }
  if (res.status === 409) return { success: true, alreadyApplied: true, application: { job_title: job.title, company: job.company } };
  if (res.status === 422 && data.profileIncomplete) return { success: false, profileIncomplete: true, missing: data.missing || [], error: data.error };
  if (!res.ok) {
    const msg = data.detail || data.error || data.message || `Server error ${res.status}`;
    throw new Error(msg);
  }
  return {
    success: true,
    alreadyApplied: false,
    application: {
      job_title: data.application?.job_title || data.job_title || job.title,
      company: data.application?.company || data.company || job.company,
      id: data.application?.id || data.id || null,
    },
  };
}

async function sendEmailsAPI({ user, job }) {
  try {
    await authFetch("/email/job-application", {
      method: "POST",
      body: JSON.stringify({
        admin_email: "ihn.socialmediaa@gmail.com",
        user_email: user.email,
        user_name: displayName(user),
        user_phone: user.phone || user.mobile || "N/A",
        user_skills: Array.isArray(user.skills) ? user.skills.join(", ") : (user.skills || "N/A"),
        user_experience: user.experience || user.work_status || "N/A",
        user_resume: user.resume_url || "N/A",
        job_title: job.title,
        job_company: job.company,
        job_id: job.id,
        recruiter_id: job.recruiter_id,
        recruiter_email: job.recruiter_email,
        applied_at: new Date().toISOString(),
      }),
    });
  } catch (e) { console.warn("Email notification failed (non-fatal):", e.message); }
}

/* ─────────────────────────────────────────
   UTILITIES
───────────────────────────────────────── */
function money(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n) || n === 0) return null;
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
}
function toList(v) {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (!v) return [];
  const t = String(v).trim();
  if (!t) return [];
  try { const p = JSON.parse(t); if (Array.isArray(p)) return p.filter(Boolean); } catch { /* noop */ }
  return t.split(",").map(s => s.trim()).filter(Boolean);
}
function parseJson(v, fb = {}) {
  if (!v) return fb;
  if (typeof v === "object") return v;
  try { const p = JSON.parse(v); return p && typeof p === "object" ? p : fb; } catch { return fb; }
}
function starLabel(n) { return ["","Poor","Below Average","Average","Good","Excellent"][n] || ""; }

/* ─────────────────────────────────────────
   AOS HOOK — pure IntersectionObserver
───────────────────────────────────────── */
function useAOS() {
  useEffect(() => {
    const nodes = [...document.querySelectorAll("[data-aos]")];
    if (!nodes.length) return undefined;

    const readNavOffset = () => {
      const parsed = Number.parseInt(getComputedStyle(document.documentElement).getPropertyValue("--cpf-nav-h"), 10);
      return Number.isFinite(parsed) ? parsed : 86;
    };
    const markVisibleNow = () => {
      const topGuard = readNavOffset() + 8;
      nodes.forEach(el => {
        const rect = el.getBoundingClientRect();
        const inViewNow = rect.top <= window.innerHeight - 24 && rect.bottom >= topGuard;
        if (inViewNow) el.classList.add("cpf-aos-in");
      });
    };

    markVisibleNow();

    const observer = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("cpf-aos-in");
          observer.unobserve(entry.target);
        }
      }),
      { threshold: 0.08, rootMargin: "0px 0px -42px 0px" }
    );
    nodes.filter(el => !el.classList.contains("cpf-aos-in")).forEach(el => observer.observe(el));

    const onResize = () => markVisibleNow();
    window.addEventListener("resize", onResize);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, []);
}

function useNavbarOffset() {
  const [offset, setOffset] = useState(86);
  useEffect(() => {
    const fallback = () => {
      const parsed = Number.parseInt(getComputedStyle(document.documentElement).getPropertyValue("--nav-h"), 10);
      return Number.isFinite(parsed) ? parsed : 86;
    };
    const sync = () => {
      const nav = document.querySelector(".nav-root");
      const h = nav?.getBoundingClientRect?.().height || nav?.offsetHeight || fallback();
      setOffset(Math.round(h));
    };
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);
  return offset;
}

/* ─────────────────────────────────────────
   PROFILE-INCOMPLETE POPUP  (JobsPage)
───────────────────────────────────────── */
function ProfileIncompletePopup({ open, onClose, user, targetJob, serverMissing }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  if (!open) return null;

  const LABEL_MAP = {
    "Full Name":         { key: "name",      icon: "👤" },
    "Email":             { key: "email",     icon: "✉️" },
    "Phone Number":      { key: "phone",     icon: "📱" },
    "Skills":            { key: "skills",    icon: "🛠️" },
    "Resume":            { key: "resume",    icon: "📄" },
    "Short Description": { key: "bio",       icon: "📝" },
    "Aadhaar Number":    { key: "aadhar",    icon: "🪪" },
    "PAN Number":        { key: "pan",       icon: "💳" },
    "Education Details": { key: "education", icon: "🎓" },
    "Country":           { key: "country",   icon: "🌍" },
    "Gender":            { key: "gender",    icon: "⚧️" },
  };

  const missing = serverMissing && serverMissing.length > 0
    ? serverMissing.map(l => ({ ...(LABEL_MAP[l] || { key: l, icon: "❗" }), label: l }))
    : getMissingFields(user);

  const pct = profilePercent(user);

  /* S3776 fix — extract progress bar color logic */
  const progressColor = pct < 80 ? "#263012" : "#111111";

  /* S3358 fix — extract target job description */
  let jobDesc;
  if (targetJob) {
    jobDesc = (
      <>To apply for <strong>{targetJob.title}</strong> at <strong>{targetJob.company}</strong>, complete missing details first.</>
    );
  } else {
    jobDesc = "Fill in required details to start applying.";
  }

  return (
    <div
      className="pip-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onClose(); }}
      role="button"
      tabIndex={0}
    >
      <dialog className="pip-modal" aria-modal="true" open>
        <div className="pip-accent-bar" />
        <button className="pip-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="pip-header">
          <div className="pip-header__icon-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
              <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
          </div>
          <div className="pip-header__text">
            <h2 className="pip-header__title">Profile Incomplete</h2>
            <p className="pip-header__sub">{jobDesc}</p>
          </div>
        </div>
        <div className="pip-progress">
          <div className="pip-progress__row">
            <span className="pip-progress__label">Profile Strength</span>
            <span className="pip-progress__pct" style={{ color: progressColor }}>{pct}%</span>
          </div>
          <div className="pip-progress__track">
            <div className="pip-progress__fill" style={{ width: `${pct}%`, background: progressColor }} />
          </div>
          <p className="pip-progress__note">{11 - missing.length} of 11 required fields filled</p>
        </div>
        <div className="pip-body">
          <p className="pip-body__heading">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            These fields are missing
          </p>
          <div className="pip-fields-list">
            {missing.map((f) => (
              <div key={f.key} className="pip-field-row">
                <div className="pip-field-row__left">
                  <span className="pip-field-row__icon">{f.icon}</span>
                  <span className="pip-field-row__label">{f.label}</span>
                </div>
                <span className="pip-field-row__tag">Required</span>
              </div>
            ))}
          </div>
        </div>
        <div className="pip-footer">
          <a href="/employee/profile" className="pip-btn pip-btn--primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Complete My Profile
          </a>
          <button className="pip-btn pip-btn--ghost" onClick={onClose}>Maybe Later</button>
        </div>
      </dialog>
    </div>
  );
}

ProfileIncompletePopup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  targetJob: PropTypes.shape({ title: PropTypes.string, company: PropTypes.string }),
  serverMissing: PropTypes.arrayOf(PropTypes.string),
};
ProfileIncompletePopup.defaultProps = { user: null, targetJob: null, serverMissing: [] };

/* ─────────────────────────────────────────
   SUCCESS POPUP  (JobsPage identical)
───────────────────────────────────────── */
function SuccessPopup({ open, job, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = "hidden";
    const t = setTimeout(onClose, 10000);
    return () => { clearTimeout(t); document.body.style.overflow = ""; };
  }, [open, onClose]);

  if (!open || !job) return null;

  return (
    <div
      className="jp-success-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onClose(); }}
      role="button"
      tabIndex={0}
    >
      <div className="jp-success-modal">
        <div className="jp-success__confetti" aria-hidden="true">
          {Array.from({ length: 18 }, (_, i) => (
            <div
              key={`confetti-${i}`}
              className="jp-success__piece"
              style={{ "--i": i, "--c": ["#263012","#263012","#111111","#1d240e","#263012","#263012","#2a2a2a","#5c774a","#263012"][i % 9] }}
            />
          ))}
        </div>
        <button className="jp-success__close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="jp-success__top">
          <div className="jp-success__emoji">🎉</div>
          <div className="jp-success__check">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="22" height="22">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        </div>
        <div className="jp-success__content">
          <h3 className="jp-success__title">Application Submitted!</h3>
          <p className="jp-success__desc">
            You&apos;ve successfully applied for <strong>{job.title || job.job_title}</strong> at <strong>{job.company}</strong>.
          </p>
          <p className="jp-success__note">The recruiter will review your profile and reach out if you&apos;re shortlisted.</p>
        </div>
        <div className="jp-success__actions">
          <a href="/employee/profile" className="jp-success__btn jp-success__btn--primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
            </svg>
            View My Applications
          </a>
          <a href="/employee/profile" className="jp-success__btn jp-success__btn--outline">Complete Profile</a>
        </div>
        <div className="jp-success__bar" />
      </div>
    </div>
  );
}

SuccessPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  job: PropTypes.shape({ title: PropTypes.string, job_title: PropTypes.string, company: PropTypes.string }),
  onClose: PropTypes.func.isRequired,
};
SuccessPopup.defaultProps = { job: null };

/* ─────────────────────────────────────────
   ERROR TOAST
───────────────────────────────────────── */
function ErrorToast({ open, message, onClose }) {
  useEffect(() => {
    if (open) {
      const t = setTimeout(onClose, 7000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", alignItems: "center", gap: 10, background: "#fef2f2", border: "1.5px solid #fca5a5", borderRadius: 12, padding: "14px 18px", boxShadow: "0 4px 24px rgba(0,0,0,0.12)", maxWidth: 420, fontSize: 14, color: "#b91c1c", fontFamily: "inherit" }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
      <span style={{ flex: 1, lineHeight: 1.4 }}>{message || "Something went wrong. Please try again."}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 0 0 8px", flexShrink: 0 }}>✕</button>
    </div>
  );
}

ErrorToast.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
ErrorToast.defaultProps = { message: "" };

/* ─────────────────────────────────────────
   JOB CARD  (pixel-identical to JobsPage jpc)
───────────────────────────────────────── */
function JobCard({ job, savedJobs, appliedJobs, applyingJobId, onToggleSave, onApplyClick, onViewDesc }) {
  const isSaved = savedJobs.has(job.id);
  const isApplied = appliedJobs.has(job.id);
  const isApplying = applyingJobId === job.id;
  const color = companyColor(job.company);
  const initial = (job.company || "?")[0].toUpperCase();

  /* S3358 fix — extract nested ternary for status badge */
  let statusBadge;
  if (isApplied) {
    statusBadge = <span className="jpc__status jpc__status--applied">✓ Applied</span>;
  } else if (job.status === "active") {
    statusBadge = <span className="jpc__status jpc__status--active">● Hiring</span>;
  } else {
    statusBadge = <span className="jpc__status jpc__status--paused">⏸ Paused</span>;
  }

  /* S3358 fix — extract nested ternary for apply button */
  let applyButton;
  if (isApplied) {
    applyButton = (
      <button className="jpc__apply jpc__apply--done" disabled>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><polyline points="20 6 9 17 4 12" /></svg>
        Applied
      </button>
    );
  } else if (isApplying) {
    applyButton = (
      <button className="jpc__apply jpc__apply--applying" disabled>
        <span className="jpc__spinner" />Applying…
      </button>
    );
  } else {
    applyButton = (
      <button className="jpc__apply" onClick={() => onApplyClick(job)} disabled={job.status === "paused"}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
        Apply Now
      </button>
    );
  }

  return (
    <div className={`jpc${isApplied ? " jpc--applied" : ""}`} style={{ "--acc": color }}>
      <div className="jpc__stripe" />
      <button
        className={`jpc__bmark${isSaved ? " jpc__bmark--on" : ""}`}
        onClick={e => { e.stopPropagation(); onToggleSave(job); }}
        title={isSaved ? "Remove saved" : "Save job"}
      >
        <svg viewBox="0 0 24 24" fill={isSaved ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" width="16" height="16">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
      </button>
      <div className="jpc__header">
        <div className="jpc__logo" style={{ background: `${color}14`, borderColor: `${color}30` }}>
          {job.logo
            ? <img src={job.logo} alt={job.company} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8 }} />
            : <span style={{ color, fontWeight: 900, fontSize: "1.1rem" }}>{initial}</span>
          }
        </div>
        <div className="jpc__header-info">
          <div className="jpc__company">{job.company}</div>
          {job.postedLabel && (
            <div className={`jpc__posted-date${job.isNew ? " jpc__posted-date--new" : ""}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {job.isNew && <span className="jpc__new-dot" />}{job.postedLabel}
            </div>
          )}
        </div>
        {statusBadge}
      </div>
      <h3 className="jpc__title">{job.title}</h3>
      <span className="jpc__dept" style={{ color, background: `${color}12`, borderColor: `${color}25` }}>{job.dept}</span>
      <div className="jpc__meta">
        {job.location && (
          <span className="jpc__chip jpc__chip--loc">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            {job.location}
          </span>
        )}
        <span className="jpc__chip jpc__chip--type">{job.type}</span>
        {job.locationType !== "On-site" && (
          <span className={`jpc__chip${job.locationType === "Remote" ? " jpc__chip--remote" : " jpc__chip--hybrid"}`}>
            {job.locationType === "Remote" ? "🌐 Remote" : "🔄 Hybrid"}
          </span>
        )}
        {job.experience && (
          <span className="jpc__chip jpc__chip--exp">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
              <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
            {job.experience}
          </span>
        )}
      </div>
      {job.salary !== "Not disclosed" && <div className="jpc__salary" style={{ color }}>{job.salary}</div>}
      {job.tags.length > 0 && (
        <div className="jpc__tags">
          {job.tags.slice(0, 4).map((t) => <span key={t} className="jpc__tag">{t}</span>)}
          {job.tags.length > 4 && <span className="jpc__tag jpc__tag--more">+{job.tags.length - 4}</span>}
        </div>
      )}
      <div className="jpc__info-bar">
        <span className="jpc__vacancy">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          {job.openings === 1 ? "1 vacancy" : `${job.openings} vacancies`}
        </span>
        <span className="jpc__info-sep">·</span>
        {job.postedLabel && (
          <span className={`jpc__date-label${job.isNew ? " jpc__date-label--new" : ""}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            Posted {job.postedLabel}
          </span>
        )}
      </div>
      <div className="jpc__actions">
        <button className="jpc__view" onClick={() => onViewDesc(job)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
          </svg>
          View Details
        </button>
        {applyButton}
      </div>
    </div>
  );
}

JobCard.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    company: PropTypes.string,
    logo: PropTypes.string,
    dept: PropTypes.string,
    type: PropTypes.string,
    location: PropTypes.string,
    locationType: PropTypes.string,
    experience: PropTypes.string,
    salary: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    openings: PropTypes.number,
    postedLabel: PropTypes.string,
    isNew: PropTypes.bool,
    status: PropTypes.string,
  }).isRequired,
  savedJobs: PropTypes.instanceOf(Set).isRequired,
  appliedJobs: PropTypes.instanceOf(Set).isRequired,
  applyingJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onToggleSave: PropTypes.func.isRequired,
  onApplyClick: PropTypes.func.isRequired,
  onViewDesc: PropTypes.func.isRequired,
};
JobCard.defaultProps = { applyingJobId: null };

/* ─────────────────────────────────────────
   JOB DETAIL MODAL  (JobsPage identical)
───────────────────────────────────────── */
function JobModal({ job, onClose, savedJobs, appliedJobs, applyingJobId, onToggleSave, onApplyClick }) {
  const isSaved = savedJobs.has(job.id);
  const isApplied = appliedJobs.has(job.id);
  const isApplying = applyingJobId === job.id;
  const color = companyColor(job.company);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => { window.removeEventListener("keydown", fn); document.body.style.overflow = ""; };
  }, [onClose]);

  /* S3358 fix — extract nested ternary for modal footer button */
  let footerApplyBtn;
  if (isApplied) {
    footerApplyBtn = <button className="jp-btn jp-btn--applied" disabled>✓ Already Applied</button>;
  } else if (isApplying) {
    footerApplyBtn = (
      <button className="jp-btn jp-btn--primary" disabled style={{ opacity: 0.75 }}>
        <span className="jpc__spinner" style={{ marginRight: 6 }} />Applying…
      </button>
    );
  } else {
    footerApplyBtn = (
      <button className="jp-btn jp-btn--primary" onClick={() => { onClose(); onApplyClick(job); }}>
        Apply Now →
      </button>
    );
  }

  return (
    <div
      className="jp-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onClose(); }}
      role="button"
      tabIndex={0}
    >
      <dialog className="jp-modal" aria-modal="true" open>
        <div className="jp-modal__head" style={{ "--acc": color }}>
          <div className="jp-modal__logo" style={{ background: `${color}15`, borderColor: `${color}30` }}>
            {job.logo
              ? <img src={job.logo} alt={job.company} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "inherit" }} />
              : <span style={{ color, fontWeight: 900, fontSize: "1.3rem" }}>{(job.company || "?")[0]}</span>
            }
          </div>
          <div className="jp-modal__head-text">
            <div className="jp-modal__title">{job.title}</div>
            <div className="jp-modal__company">{job.company}</div>
          </div>
          <button className="jp-modal__close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="jp-modal__body">
          <div className="jp-modal__chips">
            <span className="jp-meta-chip jp-meta-chip--dept" style={{ color, background: `${color}12` }}>{job.dept}</span>
            <span className="jp-meta-chip jp-meta-chip--type">{job.type}</span>
            {job.location && <span className="jp-meta-chip jp-meta-chip--loc">📍 {job.location}</span>}
            {job.experience && <span className="jp-meta-chip jp-meta-chip--exp">{job.experience}</span>}
            {job.locationType !== "On-site" && <span className="jp-meta-chip">{job.locationType}</span>}
          </div>
          <div className="jp-modal__meta-row">
            <div className="jp-modal__meta-item">
              <div className="jp-modal__meta-icon" style={{ background: `${color}18`, color }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div>
                <div className="jp-modal__meta-label">Vacancies</div>
                <div className="jp-modal__meta-val" style={{ color }}>{job.openings === 1 ? "1 opening" : `${job.openings} openings`}</div>
              </div>
            </div>
            {job.salary !== "Not disclosed" && (
              <div className="jp-modal__meta-item">
                <div className="jp-modal__meta-icon" style={{ background: "#f0fdf4", color: "#16a34a" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                </div>
                <div>
                  <div className="jp-modal__meta-label">Salary</div>
                  <div className="jp-modal__meta-val" style={{ color: "#16a34a" }}>{job.salary}</div>
                </div>
              </div>
            )}
            {job.postedLabel && (
              <div className="jp-modal__meta-item">
                <div className="jp-modal__meta-icon" style={{ background: "#f0f0f8", color: "#6b7280" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div>
                  <div className="jp-modal__meta-label">Posted</div>
                  <div className="jp-modal__meta-val">{job.postedLabel}{job.isNew && <span className="jp-modal__new-badge">NEW</span>}</div>
                </div>
              </div>
            )}
          </div>
          {job.description && (
            <div className="jp-modal__section">
              <div className="jp-modal__section-title">About this role</div>
              <div className="jp-modal__desc">{job.description}</div>
            </div>
          )}
          {job.tags.length > 0 && (
            <div className="jp-modal__section">
              <div className="jp-modal__section-title">Skills &amp; Technologies</div>
              <div className="jp-modal__tags">{job.tags.map((t) => <span key={t} className="jp-tag">{t}</span>)}</div>
            </div>
          )}
        </div>
        <div className="jp-modal__footer">
          {footerApplyBtn}
          <button
            className={`jp-icon-btn${isSaved ? " jp-icon-btn--saved" : ""}`}
            style={isSaved ? { color, borderColor: color, background: `${color}12` } : {}}
            onClick={() => onToggleSave(job)}
            title={isSaved ? "Remove saved" : "Save job"}
          >
            <svg viewBox="0 0 24 24" fill={isSaved ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
          </button>
          <button className="jp-btn jp-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </dialog>
    </div>
  );
}

JobModal.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    company: PropTypes.string,
    logo: PropTypes.string,
    dept: PropTypes.string,
    type: PropTypes.string,
    location: PropTypes.string,
    locationType: PropTypes.string,
    experience: PropTypes.string,
    salary: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    openings: PropTypes.number,
    postedLabel: PropTypes.string,
    isNew: PropTypes.bool,
    description: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  savedJobs: PropTypes.instanceOf(Set).isRequired,
  appliedJobs: PropTypes.instanceOf(Set).isRequired,
  applyingJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onToggleSave: PropTypes.func.isRequired,
  onApplyClick: PropTypes.func.isRequired,
};
JobModal.defaultProps = { applyingJobId: null };

/* ─────────────────────────────────────────
   STARS
───────────────────────────────────────── */
function CpfStars({ value, onChange, size, readOnly }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="cpf-stars" style={{ fontSize: size }}>
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          className={`cpf-star ${(hover || value) >= i ? "cpf-star--on" : ""}`}
          onClick={() => { if (!readOnly) onChange?.(i); }}
          onMouseEnter={() => { if (!readOnly) setHover(i); }}
          onMouseLeave={() => { if (!readOnly) setHover(0); }}
          disabled={readOnly}
          aria-label={`${i} star`}
        >★</button>
      ))}
      {!readOnly && hover > 0 && <span className="cpf-star-label">{starLabel(hover)}</span>}
    </div>
  );
}

CpfStars.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  size: PropTypes.number,
  readOnly: PropTypes.bool,
};
CpfStars.defaultProps = { onChange: null, size: 22, readOnly: false };

/* ─────────────────────────────────────────
   REVIEW CARD
───────────────────────────────────────── */
function CpfReviewCard({ review, isAdminUser, isRecruiterOwner, onDelete, onViewUser, onRequestRemoval }) {
  const [expanded, setExpanded] = useState(false);
  const text = review.comment || review.review || "";
  const short = text.length > 220;

  /* S3358 fix — extract nested ternary for status badge styles */
  let statusBg = "#e5e7eb";
  let statusColor = "#374151";
  if (review.status === "pending") {
    statusBg = "#fef3c7";
    statusColor = "#92400e";
  } else if (review.status === "removal_requested") {
    statusBg = "#fee2e2";
    statusColor = "#b91c1c";
  }

  return (
    <div className="cpf-review-card" data-aos="fade-up">
      <div className="cpf-review-card__head">
        <div className="cpf-review-card__avatar">{(review.user_name || review.name || "U")[0].toUpperCase()}</div>
        <div className="cpf-review-card__meta">
          <div className="cpf-review-card__name">{review.user_name || review.name || "Anonymous"}</div>
          <div className="cpf-review-card__pos">{[review.position || review.job_title, review.city || review.location].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="cpf-review-card__right">
          <CpfStars value={review.rating || 0} readOnly size={15} />
          {review.status && review.status !== "visible" && (
            <span
              className="cpf-review-card__status"
              style={{ display: "inline-flex", alignItems: "center", padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: statusBg, color: statusColor }}
            >
              {review.status.replaceAll("_", " ")}
            </span>
          )}
          <span className="cpf-review-card__date">
            {review.created_at ? new Date(review.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
          </span>
        </div>
      </div>
      {text && (
        <p className="cpf-review-card__text">
          {expanded || !short ? text : `${text.slice(0, 220)}…`}
          {short && (
            <button className="cpf-review-card__more" onClick={() => setExpanded(e => !e)}>
              {expanded ? " Show less" : " Read more"}
            </button>
          )}
        </p>
      )}
      {isAdminUser && (
        <div className="cpf-review-card__admin">
          {onViewUser && <button className="cpf-adm-btn cpf-adm-btn--view" onClick={() => onViewUser(review)}>View Profile</button>}
          {onDelete && <button className="cpf-adm-btn cpf-adm-btn--del" onClick={() => onDelete(review.id)}>Delete</button>}
        </div>
      )}
      {!isAdminUser && isRecruiterOwner && onRequestRemoval && review.status !== "removal_requested" && (
        <div className="cpf-review-card__admin">
          <button
            className="cpf-adm-btn cpf-adm-btn--view"
            onClick={() => onRequestRemoval(review)}
            style={{ background: "#f2f5ea", borderColor: "#fdba74", color: "#c2410c" }}
          >
            Request removal
          </button>
        </div>
      )}
    </div>
  );
}

CpfReviewCard.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    comment: PropTypes.string,
    review: PropTypes.string,
    user_name: PropTypes.string,
    name: PropTypes.string,
    position: PropTypes.string,
    job_title: PropTypes.string,
    city: PropTypes.string,
    location: PropTypes.string,
    rating: PropTypes.number,
    status: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
  isAdminUser: PropTypes.bool,
  isRecruiterOwner: PropTypes.bool,
  onDelete: PropTypes.func,
  onViewUser: PropTypes.func,
  onRequestRemoval: PropTypes.func,
};
CpfReviewCard.defaultProps = {
  isAdminUser: false,
  isRecruiterOwner: false,
  onDelete: null,
  onViewUser: null,
  onRequestRemoval: null,
};

/* ─────────────────────────────────────────
   ADMIN MODAL
───────────────────────────────────────── */
function CpfAdminModal({ review, onClose, onDelete }) {
  if (!review) return null;

  return (
    <div
      className="cpf-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onClose(); }}
      role="button"
      tabIndex={0}
    >
      <div className="cpf-modal">
        <div className="cpf-modal__head">
          <h3>Reviewer Profile</h3>
          <button className="cpf-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="cpf-modal__body">
          <div className="cpf-modal__avatar">{(review.user_name || "U")[0].toUpperCase()}</div>
          <div className="cpf-modal__name">{review.user_name || review.name || "Anonymous"}</div>
          <div className="cpf-modal__grid">
            {[
              ["Email",    review.user_email || review.email || "—"],
              ["Position", review.position || review.job_title || "—"],
              ["City",     review.city || review.location || "—"],
              ["Rating",   `${review.rating || 0} / 5`],
              ["Submitted", review.created_at ? new Date(review.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—"],
              ["User ID",  review.user_id || review.id || "—"],
            ].map(([l, v]) => (
              <div key={l} className="cpf-modal__item"><span>{l}</span><strong>{v}</strong></div>
            ))}
          </div>
          {(review.comment || review.review) && (
            <div className="cpf-modal__rv">
              <div className="cpf-modal__rv-label">Review</div>
              <p>{review.comment || review.review}</p>
            </div>
          )}
          <div className="cpf-modal__actions">
            <button className="cpf-modal__btn cpf-modal__btn--cancel" onClick={onClose}>Close</button>
            <button className="cpf-modal__btn cpf-modal__btn--del" onClick={() => { onDelete(review.id); onClose(); }}>Delete Review</button>
          </div>
        </div>
      </div>
    </div>
  );
}

CpfAdminModal.propTypes = {
  review: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    user_name: PropTypes.string,
    name: PropTypes.string,
    user_email: PropTypes.string,
    email: PropTypes.string,
    position: PropTypes.string,
    job_title: PropTypes.string,
    city: PropTypes.string,
    location: PropTypes.string,
    rating: PropTypes.number,
    created_at: PropTypes.string,
    user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    comment: PropTypes.string,
    review: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
CpfAdminModal.defaultProps = { review: null };

/* ─────────────────────────────────────────
   WRITE REVIEW
───────────────────────────────────────── */
function CpfWriteReview({ companyId, onSubmitted }) {
  const loggedIn = !!readStoredToken();
  const user = readStoredUser();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [selectedTag, setSelectedTag] = useState(null);
  const [writeOpen, setWriteOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const position = user?.position || user?.job_title || user?.role || "";
  const city = user?.city || user?.location || "";

  const TAG_SETS = {
    good: ["Professional","Responsive","Supportive","Transparent","Helpful"],
    avg:  ["Okay","Average","Manageable","Decent","Acceptable"],
    bad:  ["Unresponsive","Unprofessional","Rude","Misleading","Delayed"],
  };

  const getMood = r => {
    if (r >= 4) return "good";
    if (r === 3) return "avg";
    if (r > 0)  return "bad";
    return null;
  };

  const mood = getMood(rating);
  const tags = mood ? TAG_SETS[mood] : [];

  const moodStyles = {
    good: { bg: "#EAF3DE", color: "#3B6D11", border: "#639922" },
    avg:  { bg: "#FAEEDA", color: "#854F0B", border: "#BA7517" },
    bad:  { bg: "#FCEBEB", color: "#A32D2D", border: "#E24B4A" },
  };

  const handleRating = r => { setRating(r); setSelectedTag(null); setWriteOpen(false); setComment(""); };
  const handleTagClick = tag => setSelectedTag(prev => prev === tag ? null : tag);

  const handleSubmit = async () => {
    if (!rating) { setError("Please select a star rating."); return; }
    if (!selectedTag && !comment.trim()) { setError("Please pick a tag or write a review."); return; }
    setLoading(true);
    setError("");
    try {
      await apiFetch(`/api/companies/${companyId}/reviews`, {
        method: "POST",
        body: JSON.stringify({ rating, tag: selectedTag, position, city, comment: comment.trim() || null }),
      });
      setDone(true);
      onSubmitted?.();
    } catch (e) { setError(e.message || "Failed to submit review."); }
    finally { setLoading(false); }
  };

  const reset = () => { setOpen(false); setRating(0); setSelectedTag(null); setWriteOpen(false); setComment(""); setError(""); };

  /* S3358 fix — extract nested ternary for rating/mood step color */
  const getStarLabel = r => {
    if (r === 0) return null;
    return starLabel(r);
  };

  if (!loggedIn) return (
    <div className="cpf-review-cta">
      <div className="cpf-review-cta__icon">✍️</div>
      <div className="cpf-review-cta__copy">
        <div className="cpf-review-cta__title">Share your experience</div>
        <div className="cpf-review-cta__sub">Log in to rate and review this company</div>
      </div>
      <button
        className="cpf-btn cpf-btn--orange cpf-review-cta__action"
        onClick={() => window.dispatchEvent(new CustomEvent("open-login-drawer"))}
      >
        Log in to review
      </button>
    </div>
  );

  if (done) return (
    <div className="cpf-review-done">
      <span className="cpf-review-done__icon">✓</span>
      <div>
        <strong>Review submitted!</strong>
        <p>{comment.trim() ? "Your written review is pending admin approval before appearing publicly." : "Your rating has been posted successfully."}</p>
      </div>
    </div>
  );

  return (
    <div className="cpf-write-review">
      {!open
        ? <button className="cpf-btn cpf-btn--outline" onClick={() => setOpen(true)}>✍️ Write a Review</button>
        : (
          <div className="cpf-rf">
            <div className="cpf-rf__title">Your Review</div>
            <div className="cpf-rf__row">
              <label htmlFor="cpf-rating-stars">Rating <span className="cpf-req">*</span></label>
              <div id="cpf-rating-stars" className="cpf-stars" style={{ fontSize: 26 }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <button
                    key={i}
                    type="button"
                    className={`cpf-star ${(hover || rating) >= i ? "cpf-star--on" : ""}`}
                    onClick={() => handleRating(i)}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(0)}
                    aria-label={`${i} star`}
                  >★</button>
                ))}
                {hover > 0 && <span className="cpf-star-label">{getStarLabel(hover)}</span>}
              </div>
            </div>
            {mood && (
              <div className="cpf-rf__row">
                <label htmlFor="cpf-tag-group" style={{ marginBottom: 10, display: "block" }}>How would you describe it?</label>
                <div id="cpf-tag-group" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {tags.map(tag => {
                    const active = selectedTag === tag;
                    const s = moodStyles[mood];
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagClick(tag)}
                        style={{
                          padding: "7px 16px", borderRadius: 20, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                          border: active ? `1.5px solid ${s.border}` : "0.5px solid #e2e8f0",
                          background: active ? s.bg : "#f8fafc",
                          color: active ? s.color : "#374151",
                          transition: "all .15s",
                        }}
                      >
                        {active ? "✓ " : ""}{tag}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => { setWriteOpen(o => !o); if (writeOpen) setComment(""); }}
                    style={{
                      padding: "7px 16px", borderRadius: 20, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                      border: writeOpen ? "1.5px solid #cbd5e1" : "0.5px solid #e2e8f0",
                      background: writeOpen ? "#f1f5f9" : "transparent",
                      color: "#64748b",
                      transition: "all .15s",
                    }}
                  >
                    {writeOpen ? "✕ Cancel writing" : "✏ Write something else"}
                  </button>
                </div>
                {writeOpen && (
                  <div style={{ marginTop: 14 }}>
                    <textarea
                      className="cpf-rf__textarea"
                      placeholder="Share your experience in your own words…"
                      value={comment}
                      maxLength={1000}
                      onChange={e => setComment(e.target.value)}
                    />
                    <div className="cpf-rf__count">{comment.length}/1000</div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#f0f9ff", border: "0.5px solid #bae6fd", borderRadius: 10, padding: "9px 12px", fontSize: 12, color: "#0369a1", marginTop: 8 }}>
                      <span style={{ fontSize: 13, flexShrink: 0 }}>ℹ</span>
                      <span>Written reviews are reviewed by our team before appearing publicly.</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            {error && <div className="cpf-rf__error">{error}</div>}
            {mood && (
              <div className="cpf-rf__actions">
                <button className="cpf-btn cpf-btn--ghost" onClick={reset}>Cancel</button>
                <button className="cpf-btn cpf-btn--orange" onClick={handleSubmit} disabled={loading}>
                  {loading ? "Submitting…" : "Submit Review →"}
                </button>
              </div>
            )}
          </div>
        )}
    </div>
  );
}

CpfWriteReview.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSubmitted: PropTypes.func,
};
CpfWriteReview.defaultProps = { onSubmitted: null };

/* ─────────────────────────────────────────
   RATING SUMMARY
───────────────────────────────────────── */
function CpfRatingSummary({ reviews }) {
  if (!reviews.length) return null;
  const avg = reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;
  const dist = [5, 4, 3, 2, 1].map(n => ({ n, count: reviews.filter(r => r.rating === n).length }));
  return (
    <div className="cpf-rating">
      <div className="cpf-rating__big">
        <div className="cpf-rating__score">{avg.toFixed(1)}</div>
        <CpfStars value={Math.round(avg)} readOnly size={20} />
        <div className="cpf-rating__count">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</div>
      </div>
      <div className="cpf-rating__bars">
        {dist.map(({ n, count }) => (
          <div key={n} className="cpf-rating__bar-row">
            <span className="cpf-rating__bar-lbl">{n}★</span>
            <div className="cpf-rating__bar-track">
              <div className="cpf-rating__bar-fill" style={{ width: `${reviews.length ? (count / reviews.length) * 100 : 0}%` }} />
            </div>
            <span className="cpf-rating__bar-num">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

CpfRatingSummary.propTypes = {
  reviews: PropTypes.arrayOf(PropTypes.shape({ rating: PropTypes.number })).isRequired,
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
export default function CompanyProfile() {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navOffset = useNavbarOffset();

  const [company, setCompany] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adminModal, setAdminModal] = useState(null);

  const [user, setUser] = useState(() => readStoredUser());
  const [savedJobs, setSavedJobs] = useState(() => loadPersistedIds("jp_saved"));
  const [appliedJobs, setAppliedJobs] = useState(() => loadPersistedIds("jp_applied"));
  const [applyingJobId, setApplyingJobId] = useState(null);
  const applyingRef = useRef(new Set());
  const [selectedJob, setSelectedJob] = useState(null);
  const [pendingJob, setPendingJob] = useState(null);
  const [profilePopupOpen, setProfilePopupOpen] = useState(false);
  const [serverMissing, setServerMissing] = useState([]);
  const [successData, setSuccessData] = useState({ open: false, job: null });
  const [errorToast, setErrorToast] = useState({ open: false, message: "" });

  const activeTab = searchParams.get("tab") || "";
  const adminUser = isAdmin();
  const currentUser = readStoredUser();
  const isRecruiterOwner = currentUser?.role === "employer" && String(currentUser?.id) === String(companyId);

  useAOS();

  useEffect(() => { persistIds("jp_applied", appliedJobs); }, [appliedJobs]);
  useEffect(() => { persistIds("jp_saved", savedJobs); }, [savedJobs]);

  /* auth sync */
  useEffect(() => {
    const stored = readStoredUser();
    if (stored) {
      setUser(stored);
      fetchFullProfile().then(f => {
        if (!f) return;
        const m = { ...stored, ...f };
        saveUser(m, readStoredToken());
        setUser(m);
      });
    }
    const onAuth = () => { const f = readStoredUser(); if (f) setUser(f); };
    window.addEventListener("auth:update", onAuth);
    return () => window.removeEventListener("auth:update", onAuth);
  }, []);

  /* fetch company */
  useEffect(() => {
    let alive = true;
    async function load() {
      if (!companyId) { setError("Missing company ID."); setLoading(false); return; }
      setLoading(true);
      setError("");
      try {
        const data = await apiFetch(`/api/companies/${companyId}`);
        if (!alive) return;
        setCompany(data.data || data);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load company.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [companyId]);

  /* fetch reviews */
  const loadReviews = useCallback(async () => {
    if (!companyId) return;
    try {
      setReviewsLoading(true);
      const data = await apiFetch(`/api/companies/${companyId}/reviews`);
      setReviews(data.reviews || data.data || data || []);
    } catch { setReviews([]); }
    finally { setReviewsLoading(false); }
  }, [companyId]);

  useEffect(() => { loadReviews(); }, [loadReviews]);
  useEffect(() => { if (activeTab === "reviews") loadReviews(); }, [activeTab, loadReviews]);

  /* sync saved/applied from server */
  useEffect(() => {
    if (!user) return;
    authFetch("/saved-jobs").then(r => r.ok ? r.json() : null).then(d => {
      if (d?.success) {
        const ids = new Set((d.data || []).map(j => j.job_id || j.id));
        setSavedJobs(prev => { const m = new Set([...prev, ...ids]); persistIds("jp_saved", m); return m; });
      }
    }).catch(() => { /* noop */ });
    authFetch("/applications/mine").then(r => r.ok ? r.json() : null).then(d => {
      if (d?.success) {
        const ids = new Set((d.data || []).map(j => j.job_id || j.id));
        setAppliedJobs(prev => { const m = new Set([...prev, ...ids]); persistIds("jp_applied", m); return m; });
      }
    }).catch(() => { /* noop */ });
  }, [user]);

  /* ── APPLY FLOW (mirrors JobsPage exactly) ── */
  const executeApply = useCallback(async (job, currentUserRef) => {
    if (!job || !currentUserRef) return;
    if (applyingRef.current.has(job.id)) return;
    let alreadyApplied = false;
    setAppliedJobs(cur => { alreadyApplied = cur.has(job.id); return cur; });
    if (alreadyApplied) {
      setSuccessData({ open: true, job: { title: job.title, company: job.company, id: job.id } });
      return;
    }
    applyingRef.current.add(job.id);
    setApplyingJobId(job.id);
    let localUser = currentUserRef;
    try {
      const fresh = await fetchFullProfile();
      if (fresh) {
        const merged = { ...localUser, ...fresh };
        saveUser(merged, readStoredToken());
        setUser(merged);
        localUser = merged;
      }
      const result = await submitApplicationAPI({ job });
      if (result.alreadyApplied) {
        setAppliedJobs(prev => { const n = new Set([...prev, job.id]); persistIds("jp_applied", n); return n; });
        setSuccessData({ open: true, job: { title: job.title, company: job.company, id: job.id } });
        return;
      }
      if (result.profileIncomplete) {
        setPendingJob(job);
        setServerMissing(result.missing || []);
        setProfilePopupOpen(true);
        return;
      }
      if (!result.success) { setErrorToast({ open: true, message: result.error || "Application failed." }); return; }
      setAppliedJobs(prev => { const n = new Set([...prev, job.id]); persistIds("jp_applied", n); return n; });
      setSuccessData({ open: true, job: { title: result.application?.job_title || job.title, company: result.application?.company || job.company, id: job.id } });
      sendEmailsAPI({ user: localUser, job });
    } catch (err) {
      let msg = err.message || "Network error.";
      if (msg.includes("401") || msg.includes("Unauthorized")) msg = "Session expired. Please log in again.";
      else if (msg.includes("422")) msg = "Profile incomplete. Please fill all required fields.";
      else if (msg.includes("500")) msg = "Server error. Please try again in a moment.";
      setErrorToast({ open: true, message: msg });
    } finally {
      applyingRef.current.delete(job.id);
      setApplyingJobId(null);
    }
  }, []);

  const handleApplyClick = useCallback(job => {
    if (appliedJobs.has(job.id)) return;
    if (!user) {
      setPendingJob(job);
      window.dispatchEvent(new CustomEvent("open-login-drawer"));
      const onLogin = e => {
        const { token, user: u } = e.detail || {};
        if (!u || !token) return;
        saveUser(u, token);
        setUser(u);
        window.removeEventListener("login:success", onLogin);
        fetchFullProfile().then(f => { const m = f ? { ...u, ...f } : u; saveUser(m, token); setUser(m); executeApply(job, m); });
      };
      window.addEventListener("login:success", onLogin);
      return;
    }
    executeApply(job, user);
  }, [user, appliedJobs, executeApply]);

  const handleToggleSave = useCallback(job => {
    if (!user) { window.dispatchEvent(new CustomEvent("open-login-drawer")); return; }
    const saving = !savedJobs.has(job.id);
    setSavedJobs(prev => { const n = new Set(prev); if (saving) { n.add(job.id); } else { n.delete(job.id); } persistIds("jp_saved", n); return n; });
    authFetch("/saved-jobs", { method: saving ? "POST" : "DELETE", body: JSON.stringify({ job_id: job.id }) }).catch(() => { /* noop */ });
  }, [user, savedJobs]);

  /* admin */
  const handleDeleteReview = async reviewId => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await apiFetch(`/api/admin/reviews/${reviewId}`, { method: "DELETE" });
      setReviews(rs => rs.filter(r => r.id !== reviewId));
    } catch (e) { window.alert("Failed to delete review: " + e.message); }
  };

  const handleRequestRemoval = async review => {
    const reason = window.prompt("Optional reason for removal request:");
    if (reason === null) return;
    try {
      await apiFetch(`/api/reviews/${review.id}/remove-request`, { method: "POST", body: JSON.stringify({ reason }) });
      window.alert("Removal request sent to admin.");
      setReviews(rs => rs.map(r => r.id === review.id ? { ...r, status: "removal_requested" } : r));
    } catch (e) { window.alert("Failed to request removal: " + e.message); }
  };

  /* derived data */
  const rawJobs = useMemo(() => company?.jobs || [], [company]);
  const activeJobs = useMemo(() => rawJobs.map(normaliseJob).filter(j => String(j.status).toLowerCase() === "active"), [rawJobs]);
  const allJobs = useMemo(() => rawJobs.map(normaliseJob), [rawJobs]);
  const publicSec = useMemo(() => parseJson(company?.public_sections, {}), [company]);

  const sections = useMemo(() => {
    if (!company) return {};
    const s = {};
    const aboutText = publicSec.about || company.about || "";
    const facts = [
      { label: "Company size",     value: publicSec.hiring?.size || company.company_size },
      { label: "Company type",     value: publicSec.hiring?.companyType || company.company_type },
      { label: "Year established", value: publicSec.hiring?.yearEstablished || company.year_established },
      { label: "Branches",         value: publicSec.hiring?.branches || company.branches },
      { label: "Open positions",   value: publicSec.hiring?.openPositions || company.open_positions },
      { label: "Industry",         value: company.industry },
      { label: "Verified",         value: company.is_verified ? "Yes" : undefined },
    ].filter(f => f.value != null && f.value !== "" && f.value !== "0" && f.value !== 0);
    if (aboutText || facts.length) s.about = { text: aboutText, facts };

    const contact = [
      { label: "Recruiter",       value: publicSec.contact?.recruiterName || [company.role, company.dept].filter(Boolean).join(" – ") },
      { label: "Email",           value: publicSec.contact?.email || company.email || company.company_email },
      { label: "Phone",           value: publicSec.contact?.phone || company.phone },
      { label: "Alternate phone", value: publicSec.contact?.altPhone || company.alt_phone },
      { label: "Website",         value: publicSec.contact?.website || company.website },
      { label: "LinkedIn",        value: publicSec.contact?.linkedin || company.linkedin },
    ].filter(c => c.value && c.value !== "Not shared");
    if (contact.length) s.contact = contact;

    const salaries = activeJobs
      .map(j => ({ title: j.title, min: money(j.salaryMin), max: money(j.salaryMax), fixed: j.salary !== "Not disclosed" ? j.salary : null }))
      .filter(j => j.min || j.max || j.fixed);
    if (salaries.length) s.salaries = salaries;
    if (activeJobs.length) s.jobs = activeJobs;

    const benefitItems = [
      publicSec.benefits?.headline,
      ...(publicSec.benefits?.items ? [publicSec.benefits.items] : []),
      ...toList(company.employment_types).map(t => `Employment: ${t}`),
      ...toList(company.experience_levels).map(l => `Experience: ${l}`),
      company.urgency ? `Hiring urgency: ${company.urgency}` : null,
    ].filter(Boolean);
    if (benefitItems.length) s.benefits = benefitItems;

    const photos = [
      resolveLogoSrc(company.company_logo || company.logo_url || ""),
      ...(company.photos ? toList(company.photos).map(p => resolveLogoSrc(p)) : []),
    ].filter(Boolean);
    if (photos.length) s.photos = photos;

    const officeLines = [
      publicSec.hiring?.address || company.address,
      publicSec.hiring?.location || company.location,
      company.state,
      publicSec.hiring?.country || company.country,
      company.pin ? `PIN ${company.pin}` : null,
    ].filter(Boolean);
    if (officeLines.length > 1) s.offices = officeLines;

    const faq = toList(company.faq).map(f => typeof f === "string" ? { q: f, a: "" } : f).filter(f => f.q);
    if (faq.length) s.faq = faq;

    const posts = [
      ...(publicSec.posts?.headline ? [{ type: "text", content: publicSec.posts.headline }] : []),
      ...activeJobs.slice(0, 4).map(j => ({ type: "job", ...j })),
    ];
    if (posts.length) s.posts = posts;
    return s;
  }, [company, activeJobs, publicSec]);

  const visibleTabs = useMemo(() => [
    { key: "about",    label: "Overview", show: !!sections.about },
    { key: "jobs",     label: "Jobs",     show: !!sections.jobs },
    { key: "reviews",  label: "Reviews",  show: true },
    { key: "salaries", label: "Salaries", show: !!sections.salaries },
    { key: "contact",  label: "Contact",  show: !!sections.contact },
    { key: "offices",  label: "Offices",  show: !!sections.offices },
    { key: "benefits", label: "Benefits", show: !!sections.benefits },
    { key: "photos",   label: "Gallery",  show: !!sections.photos },
    { key: "posts",    label: "Posts",    show: !!sections.posts },
    { key: "faq",      label: "FAQ",      show: !!sections.faq },
  ].filter(t => t.show), [sections]);

  useEffect(() => {
    if (!loading && !error && visibleTabs.length && !visibleTabs.find(t => t.key === activeTab)) {
      setSearchParams({ tab: visibleTabs[0].key }, { replace: true });
    }
  }, [loading, error, visibleTabs, activeTab, setSearchParams]);

  const onTab = t => setSearchParams({ tab: t });

  const title = company?.company_name || "Company";
  const logo = resolveLogoSrc(company?.company_logo || company?.logo_url || "");
  const initials = title.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;
  const aboutPreview = (sections?.about?.text || company?.hiring_bio || "").trim();

  if (loading) return (
    <div className="cpf-root cpf-state-screen" style={{ "--cpf-nav-h": `${navOffset}px` }}>
      <div className="cpf-spinner" />
      <p>Loading company profile…</p>
    </div>
  );

  if (error) return (
    <div className="cpf-root cpf-state-screen" style={{ "--cpf-nav-h": `${navOffset}px` }}>
      <div className="cpf-state-screen__icon">⚠️</div>
      <h2>Could not load profile</h2>
      <p>{error}</p>
      <button className="cpf-btn cpf-btn--orange" onClick={() => navigate(-1)}>← Go back</button>
    </div>
  );

  return (
    <div className="cpf-root" style={{ "--cpf-nav-h": `${navOffset}px` }}>

      {/* ══ HERO ══ */}
      <div className="cpf-hero">
        <div className="cpf-hero__blob cpf-hero__blob--1" />
        <div className="cpf-hero__blob cpf-hero__blob--2" />
        <div className="cpf-hero__inner">
          <div className="cpf-hero__body">
            <div className="cpf-hero__logo-wrap" data-aos="fade-right">
              {logo ? <img src={logo} alt={title} className="cpf-hero__logo-img" /> : <div className="cpf-hero__logo-init">{initials}</div>}
              {company?.is_verified && <span className="cpf-hero__verified">✓</span>}
            </div>
            <div className="cpf-hero__copy" data-aos="fade-left">
              <h1 className="cpf-hero__name">{title}</h1>
              {company?.industry && <p className="cpf-hero__industry">{company.industry}</p>}
              {aboutPreview && <p className="cpf-hero__summary">{aboutPreview.slice(0, 180)}{aboutPreview.length > 180 ? "..." : ""}</p>}
              <div className="cpf-hero__pills">
                {company?.location && <span className="cpf-hero__pill">📍 {company.location}</span>}
                {activeJobs.length > 0 && <span className="cpf-hero__pill cpf-hero__pill--green">💼 {activeJobs.length} open role{activeJobs.length !== 1 ? "s" : ""}</span>}
                {company?.company_size && <span className="cpf-hero__pill">👥 {company.company_size}</span>}
                {avgRating && <span className="cpf-hero__pill cpf-hero__pill--amber">★ {avgRating} ({reviews.length})</span>}
                {[company?.company_type, company?.urgency, company?.year_established ? `Est. ${company.year_established}` : null].filter(Boolean).map(c => <span key={c} className="cpf-hero__pill cpf-hero__pill--orange">{c}</span>)}
              </div>
              <div className="cpf-hero__stats">
                {[
                  { icon: "💼", val: activeJobs.length, lbl: "Open Roles" },
                  { icon: "📋", val: allJobs.length, lbl: "Total Jobs" },
                  { icon: "⭐", val: reviews.length, lbl: "Reviews" },
                  { icon: company?.is_verified ? "✅" : "🔒", val: company?.is_verified ? "Verified" : "—", lbl: "Status" },
                ].map(h => (
                  <div key={h.lbl} className="cpf-hero__stat">
                    <span className="cpf-hero__stat-icon">{h.icon}</span>
                    <strong className="cpf-hero__stat-val">{h.val}</strong>
                    <span className="cpf-hero__stat-lbl">{h.lbl}</span>
                  </div>
                ))}
              </div>
              <div className="cpf-hero__actions">
                <button type="button" className="cpf-btn cpf-btn--orange" onClick={() => onTab("jobs")}>View Open Jobs</button>
                <button type="button" className="cpf-btn cpf-btn--outline-dark" onClick={() => onTab("reviews")}>Read Reviews</button>
                {company?.website && <a className="cpf-btn cpf-btn--ghost-dark" href={company.website.startsWith("http") ? company.website : `https://${company.website}`} target="_blank" rel="noreferrer">Website ↗</a>}
                {company?.linkedin && <a className="cpf-btn cpf-btn--ghost-dark" href={company.linkedin.startsWith("http") ? company.linkedin : `https://${company.linkedin}`} target="_blank" rel="noreferrer">LinkedIn ↗</a>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ TABS ══ */}
      <div className="cpf-tabs-bar">
        <div className="cpf-tabs-bar__inner">
          {visibleTabs.map(t => (
            <button key={t.key} className={`cpf-tab ${activeTab === t.key ? "cpf-tab--active" : ""}`} onClick={() => onTab(t.key)}>
              {t.label}
              {t.key === "jobs" && sections.jobs && <span className="cpf-tab__badge">{sections.jobs.length}</span>}
              {t.key === "reviews" && reviews.length > 0 && <span className="cpf-tab__badge">{reviews.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div className="cpf-body">

        {/* ── OVERVIEW ── */}
        {activeTab === "about" && sections.about && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-overview-hero" data-aos="fade-up">
              <div className="cpf-overview-hero__brand">
                <div className="cpf-overview-hero__logo">
                  {logo ? <img src={logo} alt={title} /> : <span>{initials}</span>}
                </div>
                <div>
                  <div className="cpf-overview-hero__name">{title}</div>
                  {company?.industry && <div className="cpf-overview-hero__ind">{company.industry}</div>}
                  {company?.location && <div className="cpf-overview-hero__loc">📍 {company.location}</div>}
                </div>
              </div>
              <div className="cpf-overview-hero__stats">
                {[
                  { n: activeJobs.length, l: "Open Roles",  a: "#263012" },
                  { n: allJobs.length,    l: "Total Jobs",  a: "#263012" },
                  { n: reviews.length,    l: "Reviews",     a: "#2563eb" },
                  { n: avgRating ? `${avgRating}★` : "—", l: "Avg Rating", a: "#059669" },
                ].map(h => (
                  <div key={h.l} className="cpf-overview-hero__stat" style={{ "--a": h.a }}>
                    <span className="cpf-overview-hero__stat-val">{h.n}</span>
                    <span className="cpf-overview-hero__stat-lbl">{h.l}</span>
                  </div>
                ))}
              </div>
            </div>

            {sections.about.text && (
              <div className="cpf-card" data-aos="fade-up">
                <div className="cpf-card__head"><h3 className="cpf-card__title">About {title}</h3><span className="cpf-card__badge">Overview</span></div>
                <p className="cpf-about-text">{sections.about.text}</p>
              </div>
            )}

            {sections.about.facts.length > 0 && (
              <div className="cpf-card" data-aos="fade-up">
                <div className="cpf-card__head"><h3 className="cpf-card__title">Company Details</h3><span className="cpf-card__badge">{sections.about.facts.length} details</span></div>
                <div className="cpf-facts-grid">
                  {sections.about.facts.map((f, i) => (
                    <div key={f.label} className="cpf-fact" data-aos="fade-up" style={{ "--d": `${i * 40}ms` }}>
                      <span className="cpf-fact__lbl">{f.label}</span>
                      <strong className="cpf-fact__val">{f.value}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="cpf-why-row" data-aos="fade-up">
              {[
                { icon: "🚀", title: "Active Hiring",      desc: `${activeJobs.length} open position${activeJobs.length !== 1 ? "s" : ""} right now` },
                { icon: "⭐", title: "Community Rated",    desc: `${reviews.length} review${reviews.length !== 1 ? "s" : ""} from real candidates` },
                { icon: company?.is_verified ? "✅" : "🔒", title: company?.is_verified ? "Verified Employer" : "Listed Employer", desc: company?.is_verified ? "Identity & credentials confirmed" : "Profile pending verification" },
              ].map((w, i) => (
                <div key={w.title} className="cpf-why-card" data-aos="fade-up" style={{ "--d": `${i * 80}ms` }}>
                  <div className="cpf-why-card__icon">{w.icon}</div>
                  <div className="cpf-why-card__title">{w.title}</div>
                  <div className="cpf-why-card__desc">{w.desc}</div>
                </div>
              ))}
            </div>

            {sections.jobs && (
              <div className="cpf-card" data-aos="fade-up">
                <div className="cpf-card__head">
                  <h3 className="cpf-card__title">Latest Openings</h3>
                  <button className="cpf-card__link" onClick={() => onTab("jobs")}>View all {sections.jobs.length} →</button>
                </div>
                {user && <div className="cpf-jobs-notice">👋 Logged in as <strong>{displayName(user)}</strong> — click Apply Now to submit directly</div>}
                <div className="cpf-jobs-preview-grid">
                  {sections.jobs.slice(0, 3).map(job => (
                    <JobCard
                      key={job.id} job={job} savedJobs={savedJobs} appliedJobs={appliedJobs}
                      applyingJobId={applyingJobId} onToggleSave={handleToggleSave}
                      onApplyClick={handleApplyClick} onViewDesc={setSelectedJob}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── JOBS ── */}
        {activeTab === "jobs" && sections.jobs && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-card" data-aos="fade-up">
              <div className="cpf-card__head">
                <h3 className="cpf-card__title">Open Positions at {title}</h3>
                <span className="cpf-card__badge">{sections.jobs.length} active</span>
              </div>
              {user && <div className="cpf-jobs-notice">👋 Logged in as <strong>{displayName(user)}</strong> — click Apply Now on any card to apply instantly</div>}
              {!user && (
                <div className="cpf-jobs-notice cpf-jobs-notice--warn">
                  🔒{" "}
                  <button
                    type="button"
                    className="cpf-link-btn"
                    onClick={() => window.dispatchEvent(new CustomEvent("open-login-drawer"))}
                  >
                    Log in
                  </button>
                  {" "}to apply for positions at {title}
                </div>
              )}
              <div className="cpf-jobs-grid">
                {sections.jobs.map((job, i) => (
                  <div key={job.id} data-aos="fade-up" style={{ "--d": `${(i % 6) * 60}ms` }}>
                    <JobCard
                      job={job} savedJobs={savedJobs} appliedJobs={appliedJobs}
                      applyingJobId={applyingJobId} onToggleSave={handleToggleSave}
                      onApplyClick={handleApplyClick} onViewDesc={setSelectedJob}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── REVIEWS ── */}
        {activeTab === "reviews" && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-two-col">
              <div className="cpf-main">
                {reviews.length > 0 && (
                  <div className="cpf-card" data-aos="fade-up">
                    <h3 className="cpf-card__title">Rating Overview</h3>
                    <CpfRatingSummary reviews={reviews} />
                  </div>
                )}
                <div className="cpf-card" data-aos="fade-up">
                  <h3 className="cpf-card__title">Write a Review</h3>
                  <CpfWriteReview companyId={companyId} onSubmitted={loadReviews} />
                </div>
                {reviews.length > 0 ? (
                  <div className="cpf-card" data-aos="fade-up">
                    <div className="cpf-card__head">
                      <h3 className="cpf-card__title">All Reviews</h3>
                      <span className="cpf-card__badge">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</span>
                    </div>
                    {reviewsLoading && <div style={{ padding: "10px 0", color: "#64748b", fontSize: 13 }}>Loading reviews...</div>}
                    <div className="cpf-review-list">
                      {reviews.map(r => (
                        <CpfReviewCard
                          key={r.id} review={r} isAdminUser={adminUser} isRecruiterOwner={isRecruiterOwner}
                          onDelete={adminUser ? handleDeleteReview : null}
                          onViewUser={adminUser ? setAdminModal : null}
                          onRequestRemoval={!adminUser && isRecruiterOwner ? handleRequestRemoval : null}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="cpf-card cpf-empty" data-aos="fade-up">
                    <div className="cpf-empty__icon">💬</div>
                    <div className="cpf-empty__title">{reviewsLoading ? "Loading reviews..." : "No reviews yet"}</div>
                    <div className="cpf-empty__sub">{reviewsLoading ? `Fetching reviews for ${title}…` : `Be the first to share your experience with ${title}`}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── SALARIES ── */}
        {activeTab === "salaries" && sections.salaries && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-card" data-aos="fade-up">
              <div className="cpf-card__head"><h3 className="cpf-card__title">Salary Ranges</h3><span className="cpf-card__badge">{sections.salaries.length} roles</span></div>
              <p className="cpf-card__intro">Compensation sourced from active job postings. Actual salaries may vary.</p>
              <div className="cpf-sal-list">
                {sections.salaries.map((s, i) => (
                  <div key={s.title} className="cpf-sal-row" data-aos="fade-up" style={{ "--d": `${i * 50}ms` }}>
                    <div className="cpf-sal-row__title">{s.title}</div>
                    <div className="cpf-sal-row__range">{s.fixed || [s.min, s.max].filter(Boolean).join(" – ") || "Not disclosed"}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── CONTACT ── */}
        {activeTab === "contact" && sections.contact && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-card" data-aos="fade-up">
              <div className="cpf-card__head"><h3 className="cpf-card__title">Contact Information</h3><span className="cpf-card__badge">Public details</span></div>
              <p className="cpf-card__intro">Publicly visible contact details provided by the employer.</p>
              <div className="cpf-contact-grid">
                {sections.contact.map((c, i) => (
                  <div key={c.label} className="cpf-contact-item" data-aos="fade-up" style={{ "--d": `${i * 60}ms` }}>
                    <span className="cpf-contact-item__lbl">{c.label}</span>
                    <strong className="cpf-contact-item__val">
                      {c.label === "Website" || c.label === "LinkedIn"
                        ? <a href={c.value.startsWith("http") ? c.value : `https://${c.value}`} target="_blank" rel="noreferrer">{c.value}</a>
                        : c.value}
                    </strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── OFFICES ── */}
        {activeTab === "offices" && sections.offices && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-card" data-aos="fade-up">
              <div className="cpf-card__head"><h3 className="cpf-card__title">Office Location</h3><span className="cpf-card__badge">Address details</span></div>
              <div className="cpf-office-list">
                {sections.offices.map((line, i) => (
                  <div key={line} className="cpf-office-line" data-aos="fade-right" style={{ "--d": `${i * 60}ms` }}>
                    <span className="cpf-office-line__icon">{i === 0 ? "🏢" : i === 1 ? "📍" : "📌"}</span>{line}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── BENEFITS ── */}
        {activeTab === "benefits" && sections.benefits && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-card" data-aos="fade-up">
              <div className="cpf-card__head"><h3 className="cpf-card__title">Benefits &amp; Perks</h3><span className="cpf-card__badge">{sections.benefits.length} items</span></div>
              <div className="cpf-benefits-list">
                {sections.benefits.map((b, i) => (
                  <div key={b} className="cpf-benefit-item" data-aos="fade-right" style={{ "--d": `${i * 50}ms` }}>
                    <span className="cpf-benefit-item__dot" />{b}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── GALLERY ── */}
        {activeTab === "photos" && sections.photos && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-card" data-aos="fade-up">
              <div className="cpf-card__head"><h3 className="cpf-card__title">Gallery</h3><span className="cpf-card__badge">{sections.photos.length} photos</span></div>
              <div className="cpf-photo-grid">
                {sections.photos.map((p, i) => (
                  <img
                    key={p}
                    src={p}
                    alt={`${title} workplace photo`}
                    className="cpf-photo"
                    data-aos="fade-up"
                    style={{ "--d": `${i * 60}ms` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── POSTS ── */}
        {activeTab === "posts" && sections.posts && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-card" data-aos="fade-up">
              <div className="cpf-card__head"><h3 className="cpf-card__title">Latest Posts</h3><span className="cpf-card__badge">{sections.posts.length} posts</span></div>
              <div className="cpf-posts-list">
                {sections.posts.map((p, i) => (
                  <div key={p.type === "text" ? `text-${i}` : p.id} className="cpf-post-item" data-aos="fade-up" style={{ "--d": `${i * 60}ms` }}>
                    {p.type === "text"
                      ? <p>{p.content}</p>
                      : <div className="cpf-post-item__job"><div className="cpf-post-item__job-title">{p.title}</div><span className="cpf-post-item__job-tag">New Opening</span></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── FAQ ── */}
        {activeTab === "faq" && sections.faq && (
          <div className="cpf-sec cpf-sec--anim">
            <div className="cpf-card" data-aos="fade-up">
              <div className="cpf-card__head"><h3 className="cpf-card__title">Frequently Asked Questions</h3><span className="cpf-card__badge">{sections.faq.length} questions</span></div>
              <div className="cpf-faq-list">
                {sections.faq.map((f, i) => (
                  <div key={f.q} className="cpf-faq-item" data-aos="fade-up" style={{ "--d": `${i * 60}ms` }}>
                    <div className="cpf-faq-item__q">Q: {f.q}</div>
                    {f.a && <div className="cpf-faq-item__a">A: {f.a}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>{/* end cpf-body */}

      {/* ── JOB DETAIL MODAL ── */}
      {selectedJob && (
        <JobModal
          job={selectedJob} onClose={() => setSelectedJob(null)}
          savedJobs={savedJobs} appliedJobs={appliedJobs} applyingJobId={applyingJobId}
          onToggleSave={handleToggleSave} onApplyClick={handleApplyClick}
        />
      )}

      {/* ── ADMIN MODAL ── */}
      {adminModal && <CpfAdminModal review={adminModal} onClose={() => setAdminModal(null)} onDelete={handleDeleteReview} />}

      {/* ── PROFILE INCOMPLETE (JobsPage-identical) ── */}
      <ProfileIncompletePopup
        open={profilePopupOpen}
        onClose={() => { setProfilePopupOpen(false); setPendingJob(null); setServerMissing([]); }}
        user={user} targetJob={pendingJob} serverMissing={serverMissing}
      />

      {/* ── SUCCESS (JobsPage-identical) ── */}
      <SuccessPopup
        key={`${successData.job?.id}-${successData.open}`}
        open={successData.open} job={successData.job}
        onClose={() => setSuccessData({ open: false, job: null })}
      />

      {/* ── ERROR TOAST ── */}
      <ErrorToast
        open={errorToast.open} message={errorToast.message}
        onClose={() => setErrorToast({ open: false, message: "" })}
      />
    </div>
  );
}